import { gql } from "@apollo/client";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  IconButton,
  Typography,
} from "@mui/material";
import Close from "@mui/icons-material/Close";
import { set } from "date-fns";
import { useState } from "react";

import { CalendarBlockBlockTypeEnum, CreateCalendarBlockMutationVariables, useCreateCalendarBlockMutation } from "~/graphql/__generated__/graphql";
import CalendarBlockEditForm, { isCalendarBlockValid } from "./CalendarBlockEditForm";
import { defaultCalendarBlockTitles, getDefaultFilters } from "@caredna-marlin/utils/calendarBlockHelper";

type AddCalendarBlockProps = {
  date: Date;
  blockType: CalendarBlockBlockTypeEnum;
  onClose: () => void;
  /**
   * When set, the block belongs to this provider: the id is seeded and the
   * provider select is hidden. Callers that leave it undefined are unchanged.
   */
  lockedProviderId?: number;
  /** Called with the new block's id once the mutation has resolved, before onClose. */
  onCreated?: (id: number, block: CreateCalendarBlockMutationVariables) => void;
};

const AddCalendarBlock = ({ date, blockType, onClose, lockedProviderId, onCreated }: AddCalendarBlockProps) => {
  const [createCalendarBlock, { loading }] = useCreateCalendarBlockMutation();
  const [block, setBlock] = useState<CreateCalendarBlockMutationVariables>({
    title: defaultCalendarBlockTitles[blockType],
    providerId: lockedProviderId ?? 0,
    startTime: set(date, {hours: 9, minutes: 0, seconds: 0, milliseconds: 0}).toString(),
    endTime: set(date, {hours: 17, minutes: 0, seconds: 0, milliseconds: 0}).toString(),
    addressStreet: "",
    addressLineTwo: "",
    addressCity: "",
    addressState: "",
    addressZip: "",
    addressTimezone: "",
    addressPlaceId: "",
    blockType,
    filters: getDefaultFilters(blockType),
  });

  const titleLabel = `Add ${defaultCalendarBlockTitles[blockType]?.toLowerCase()}`;

  const onSave = async () => {
    const { data } = await createCalendarBlock({
      variables: block,
      refetchQueries: ["CommandIndexCombinedData", "VisitAlerts", "VisitLocations", "AvailabilityByTimespan"],
      awaitRefetchQueries: true,
    });
    const id = data?.createCalendarBlock?.id;
    if (id !== undefined && id !== null) onCreated?.(id, block);
    onClose();
  };

  return (
    <Card elevation={3} sx={{ width: 550 }}>
      <Header onClose={onClose} titleLabel={titleLabel} />
      <CardContent>
        <CalendarBlockEditForm
          calendarBlock={block}
          onChange={setBlock}
          lockedProviderId={lockedProviderId}
        />
      </CardContent>

      <CardActions sx={{ justifyContent: "end", borderTop: 1, borderColor: "divider" }}>
        <Button color="primary" onClick={onSave} loading={loading} disabled={!isCalendarBlockValid(block)}>Save</Button>
      </CardActions>
    </Card>
  );
};

const Header = ({ onClose, titleLabel }: { onClose: () => void, titleLabel: string }) => (<CardHeader
  sx={{
    borderBottom: 1,
    borderColor: "divider",
    flex: 1,
    flexDirection: "row",
  }}
  action={<IconButton aria-label="close" onClick={onClose}>
    <Close />
  </IconButton>}
  title={<Typography variant="h6">{titleLabel}</Typography>}
  />
);

export default AddCalendarBlock;

gql(`
  mutation CreateCalendarBlock(
    $providerId: Int!
    $startTime: String!
    $endTime: String!
    $title: String
    $addressStreet: String
    $addressLineTwo: String
    $addressCity: String
    $addressState: String
    $addressZip: String
    $addressTimezone: String
    $filters: CalendarBlockFilterInput
    $blockType: CalendarBlockBlockTypeEnum!
    $addressPlaceId: String
  )
  @permission(requires: manageCalendarBlocks)
  {
    createCalendarBlock(
      providerId: $providerId
      startTime: $startTime
      endTime: $endTime
      title: $title
      addressStreet: $addressStreet
      addressLineTwo: $addressLineTwo
      addressCity: $addressCity
      addressState: $addressState
      addressZip: $addressZip
      addressTimezone: $addressTimezone
      filters: $filters,
      blockType: $blockType
      addressPlaceId: $addressPlaceId
    ) { id }
  }
`);
